// UploadOnBehalfModal.jsx - Head marker uploads a submission on behalf of a student
// (e.g. student emailed the file directly, or their own upload was corrupted)
import React, { useState, useEffect } from 'react';
import { Upload } from 'lucide-react';
import { getCohortStudents } from '../firebase/cohorts';
import { headMarkerUploadSubmission } from '../firebase/submissions';

function UploadOnBehalfModal({
  show,
  onClose,
  cohorts,
  homeworkTypes,
  homeworkNames,
  onUploaded
}) {
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedHomeworkType, setSelectedHomeworkType] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const resetState = () => {
    setSelectedCohortId('');
    setStudents([]);
    setSelectedStudentId('');
    setSelectedHomeworkType('');
    setSelectedFile(null);
    setError('');
    setUploading(false);
    setUploadProgress(0);
  };

  useEffect(() => {
    if (!show) resetState();
  }, [show]);

  useEffect(() => {
    if (!selectedCohortId) {
      setStudents([]);
      setSelectedStudentId('');
      return;
    }
    setLoadingStudents(true);
    setSelectedStudentId('');
    getCohortStudents(selectedCohortId)
      .then(setStudents)
      .catch(err => {
        console.error('Failed to load students for cohort:', err);
        setError('Could not load students for this cohort.');
      })
      .finally(() => setLoadingStudents(false));
  }, [selectedCohortId]);

  if (!show) return null;

  const handleClose = () => {
    if (uploading) return;
    onClose();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setError('');
    if (!file) {
      setSelectedFile(null);
      return;
    }
    if (file.type !== 'application/pdf') {
      setError('Must be a PDF file');
      setSelectedFile(null);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const handleSubmit = async () => {
    if (!selectedCohortId || !selectedStudentId || !selectedHomeworkType || !selectedFile) {
      setError('Please select a cohort, student, homework type, and PDF file.');
      return;
    }

    const cohort = cohorts.find(c => c.id === selectedCohortId);
    const student = students.find(s => s.id === selectedStudentId);

    setUploading(true);
    setError('');
    setUploadProgress(0);

    try {
      await headMarkerUploadSubmission({
        studentId: student.id,
        studentName: student.name,
        studentEmail: student.email,
        studentPhone: student.phone || '',
        cohortId: selectedCohortId,
        cohortName: cohort?.name || '',
        homeworkType: selectedHomeworkType,
        file: selectedFile,
        onProgress: setUploadProgress
      });

      onUploaded();
      resetState();
      onClose();
    } catch (err) {
      console.error('Upload on behalf of student error:', err);
      setError(err.message || 'Failed to upload submission.');
      setUploading(false);
    }
  };

  return (
    <>
      <div
        onClick={handleClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 999
        }}
      />

      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'white',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        zIndex: 1000,
        minWidth: '440px',
        maxWidth: '90vw'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#2c3e50' }}>
          Upload Submission for Student
        </h3>
        <p style={{ color: '#7f8c8d', marginBottom: '16px', fontSize: '14px' }}>
          Use this only when a student can't submit themselves (e.g. emailed the file directly, or their own upload was corrupted and has been reset).
        </p>

        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#2c3e50', marginBottom: '6px' }}>
          Cohort
        </label>
        <select
          value={selectedCohortId}
          onChange={(e) => setSelectedCohortId(e.target.value)}
          disabled={uploading}
          style={{
            width: '100%',
            padding: '12px',
            border: '2px solid #ecf0f1',
            borderRadius: '8px',
            fontSize: '14px',
            fontFamily: 'inherit',
            marginBottom: '16px'
          }}
        >
          <option value="">Select a cohort...</option>
          {cohorts.map(cohort => (
            <option key={cohort.id} value={cohort.id}>{cohort.name}</option>
          ))}
        </select>

        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#2c3e50', marginBottom: '6px' }}>
          Student
        </label>
        <select
          value={selectedStudentId}
          onChange={(e) => setSelectedStudentId(e.target.value)}
          disabled={uploading || !selectedCohortId || loadingStudents}
          style={{
            width: '100%',
            padding: '12px',
            border: '2px solid #ecf0f1',
            borderRadius: '8px',
            fontSize: '14px',
            fontFamily: 'inherit',
            marginBottom: '16px'
          }}
        >
          <option value="">
            {loadingStudents ? 'Loading students...' : 'Select a student...'}
          </option>
          {students.map(student => (
            <option key={student.id} value={student.id}>{student.name}</option>
          ))}
        </select>

        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#2c3e50', marginBottom: '6px' }}>
          Homework Type
        </label>
        <select
          value={selectedHomeworkType}
          onChange={(e) => setSelectedHomeworkType(e.target.value)}
          disabled={uploading}
          style={{
            width: '100%',
            padding: '12px',
            border: '2px solid #ecf0f1',
            borderRadius: '8px',
            fontSize: '14px',
            fontFamily: 'inherit',
            marginBottom: '16px'
          }}
        >
          <option value="">Select homework type...</option>
          {homeworkTypes.map(type => (
            <option key={type} value={type}>{homeworkNames?.[type] || type}</option>
          ))}
        </select>

        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#2c3e50', marginBottom: '6px' }}>
          PDF File
        </label>
        <input
          type="file"
          accept="application/pdf"
          onChange={handleFileChange}
          disabled={uploading}
          style={{
            width: '100%',
            padding: '10px',
            border: '2px solid #ecf0f1',
            borderRadius: '8px',
            fontSize: '14px',
            fontFamily: 'inherit',
            marginBottom: '16px'
          }}
        />

        {uploading && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ background: '#ecf0f1', borderRadius: '8px', overflow: 'hidden', height: '8px' }}>
              <div style={{
                width: `${uploadProgress}%`,
                background: '#1b998b',
                height: '100%',
                transition: 'width 0.2s'
              }} />
            </div>
            <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
              Uploading... {uploadProgress}%
            </div>
          </div>
        )}

        {error && (
          <div style={{
            background: '#fff5f5',
            color: '#e74c3c',
            padding: '10px 12px',
            borderRadius: '8px',
            fontSize: '13px',
            marginBottom: '16px'
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleClose}
            disabled={uploading}
            style={{
              padding: '12px 24px',
              background: '#ecf0f1',
              color: '#2c3e50',
              border: 'none',
              borderRadius: '8px',
              cursor: uploading ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={uploading}
            style={{
              padding: '12px 24px',
              background: '#1b998b',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: uploading ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Upload size={16} />
            {uploading ? 'Uploading...' : 'Upload Submission'}
          </button>
        </div>
      </div>
    </>
  );
}

export default UploadOnBehalfModal;
